/**
 * Read the file in the browser and say what is wrong with it, BEFORE any of it is uploaded.
 *
 * Uploading first and validating afterwards costs the person the whole transfer before
 * telling them the file was the wrong form, or empty, or a spreadsheet with no header row -
 * and leaves a file in storage that will never be filed. Everything below happens in the tab,
 * against the file on their own disk, and nothing is sent until it passes.
 *
 * How far each format is checked, stated plainly because the difference matters:
 *
 *   IRS 1220   fully. parsePub1220 and checkRecords are the same code the server validates
 *              with, so a file that passes here passes there for the same reasons.
 *   CSV/Excel  fully. The headers are mapped with the same detectColumnMapping the server
 *              uses, the rows become canonical records, and checkRecords runs on them. The
 *              mapping, the ignored columns and the box totals are returned for review, so
 *              the person sees what will be filed before a byte of it is uploaded.
 *   IRIS XML   well formed, and carries returns. The IRS schema check needs a native XML
 *              library that does not exist in a browser; the server does it.
 *
 * The server re-validates everything regardless. This is a courtesy to the person, never the
 * authority on whether something may be filed.
 */
import { parsePub1220, type Pub1220PayeeName } from '@engine/intake/pub1220';
import {
  canonicalNamesFromJsonSchema,
  detectColumnMapping,
  rowToRecord,
} from '@engine/intake/columnMapping';
import { boxTotals, type BoxTotal } from '@engine/validate/boxTotals';
import { checkRecords } from '@engine/validate/checkRecords';
import { FORM_FIELDS } from '@engine/validate/formFields';
import { FORM_IDENTITY } from '@engine/validate/fieldMap';
import { recordJsonSchema } from '@engine/validate/schemas';

/**
 * What the check is doing right now, so the person can watch it happen. A large 1220 file
 * takes real seconds to read and check, and a check nobody can see is one nobody believes
 * happened - which is the whole reason it was moved in front of the upload.
 */
export type PrecheckPhase = 'reading' | 'parsing' | 'checking';

/** Hand control back to the browser so it can paint the phase just set. */
const paint = () => new Promise<void>((resolve) => window.setTimeout(resolve, 0));

/** The server's own limit, so the browser refuses the same files it would. */
export const MAX_ROWS = 500_000;

export interface PrecheckIssue {
  severity: 'blocking' | 'warning';
  title: string;
  detail: string;
  /** Which line or row, when the fault has one. */
  where?: string;
}

export interface PrecheckResult {
  ok: boolean;
  issues: PrecheckIssue[];
  /** What the file turned out to be, when it says so itself. */
  detected: { formType?: string; taxYear?: number; recordCount?: number };
  /** How thoroughly this format could be checked here. */
  depth: 'full' | 'structural';
  /** Row files: source header -> canonical column, the way the server will read it. */
  mapping?: Record<string, string>;
  /** Row files: headers that matched no column on the form. Ignored, never guessed at. */
  unmapped?: string[];
  /**
   * The rows as the records the server will file, keyed by field name. Kept for a CSV or Excel
   * file so Resolve Names can read the recipient names here, before anything is uploaded.
   */
  records?: Record<string, unknown>[];
  /**
   * An IRS 1220 file: the one name line each payee was written with. The file has no split name
   * columns, so Resolve Names shows how each line was read before anything is uploaded.
   */
  payeeNames?: Pub1220PayeeName[];
  /** What the records add up to, box by box, for the person to check against their own. */
  boxTotals?: BoxTotal[];
}

const blocking = (title: string, detail: string, where?: string): PrecheckIssue => ({
  severity: 'blocking',
  title,
  detail,
  ...(where ? { where } : {}),
});

/** A fixed-width file must be read as single-byte, or every column after a stray byte shifts. */
async function readLatin1(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  return new TextDecoder('latin1').decode(buffer);
}

async function precheckPub1220(
  file: File,
  taxYear: number,
  onPhase: (phase: PrecheckPhase) => void,
): Promise<PrecheckResult> {
  onPhase('reading');
  await paint();
  const text = await readLatin1(file);
  onPhase('parsing');
  await paint();
  const parsed = parsePub1220(text);
  const issues: PrecheckIssue[] = [];

  for (const error of parsed.errors.slice(0, 20)) {
    issues.push(
      blocking('This file does not follow Publication 1220', error.message, `line ${error.line}`),
    );
  }
  if (parsed.errors.length > 20) {
    issues.push(
      blocking(
        `${parsed.errors.length - 20} more lines could not be read`,
        'Only the first twenty are listed. A file this far out is usually an export problem rather than a data problem.',
      ),
    );
  }

  if (parsed.formTypes.length > 1) {
    issues.push(
      blocking(
        'This file holds more than one form type',
        `It carries ${parsed.formTypes.join(', ')}. One e-file is one form type; split the file and submit each part.`,
      ),
    );
  }
  if (parsed.records.length === 0 && parsed.errors.length === 0) {
    issues.push(blocking('There are no payee records in this file', 'A 1220 file files its "B" records; this one has none.'));
  }
  if (parsed.records.length > MAX_ROWS) {
    issues.push(
      blocking(
        'This file has too many records',
        `${parsed.records.length.toLocaleString()} records, and the limit is ${MAX_ROWS.toLocaleString()}.`,
      ),
    );
  }

  // Two payee records claiming the same account number. Position stands in for a missing
  // one, exactly as the server does it, so records without an account number never collide.
  issues.push(
    ...duplicateReferenceIssues(
      parsed.records.map((record, index) => ({
        ref: typeof record.recordRef === 'string' ? record.recordRef.trim() : '',
        line: index + 1,
      })),
      (ref, line) =>
        blocking(
          'Two payee records share an account number',
          `Two payee records share the account number "${ref}". It is what names a return if you ` +
            'correct or replace it later, so two returns cannot share one. A record with no ' +
            'account number is referenced by its position in the file instead.',
          `payee record ${line}`,
        ),
    ),
  );

  // The records themselves, through the gate the server uses.
  let totals: BoxTotal[] | undefined;
  if (parsed.formType && parsed.records.length > 0) {
    onPhase('checking');
    await paint();
    const year = parsed.taxYear ?? taxYear;
    issues.push(...recordIssues(parsed.formType, year, parsed.records));
    totals = boxTotals(parsed.formType, year, parsed.records);
  }

  return {
    ok: issues.length === 0,
    issues,
    detected: {
      formType: parsed.formType ?? undefined,
      taxYear: parsed.taxYear ?? undefined,
      recordCount: parsed.records.length,
    },
    depth: 'full',
    boxTotals: totals,
    ...(parsed.formType && parsed.payeeNames.length > 0 ? { payeeNames: parsed.payeeNames } : {}),
  };
}

/**
 * The engine's record checks, grouped the way a person reads them: one line per kind of
 * fault, how many records have it, and where the first one is. Only errors block; the
 * server reports warnings with the upload.
 */
function recordIssues(formType: string, taxYear: number, records: Record<string, unknown>[]): PrecheckIssue[] {
  const grouped = new Map<string, { count: number; message: string; first: string }>();
  for (const issue of checkRecords(formType, taxYear, records).issues) {
    if (issue.severity !== 'error') continue;
    // One line per kind of fault: code, field and rule (TS-8704).
    const key = `${issue.code}:${issue.field ?? ''}:${issue.rule ?? ''}`;
    const seen = grouped.get(key);
    if (seen) seen.count += 1;
    else
      grouped.set(key, {
        count: 1,
        message: issue.message,
        first: String(issue.recordRef ?? `row ${issue.index + 1}`),
      });
  }
  return [...grouped.values()].map((group) =>
    blocking(
      `${group.count} record${group.count === 1 ? '' : 's'} the IRS will not accept`,
      group.message,
      `first at ${group.first}`,
    ),
  );
}

/** Header names normalise the way the server's column matching does. */
const normalize = (value: string): string => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * The header that supplies each return's reference, in the order the server prefers them.
 * Mirrors RECORD_REF_HEADERS in the API's column mapping; a file with none of these has its
 * rows referenced by position instead, and positions cannot collide.
 */
const RECORD_REF_HEADERS = ['recordRef', 'RECORD_REF', 'ACCOUNT_NUMBER'];

function findRecordRefHeader(headers: string[]): string | null {
  for (const wanted of RECORD_REF_HEADERS) {
    const shape = normalize(wanted);
    const found = headers.find((header) => normalize(header) === shape);
    if (found) return found;
  }
  return null;
}

/**
 * Two returns claiming the same reference.
 *
 * The reference is what a correction or a replacement names later, so the server refuses a
 * file where two returns share one - and it was refusing it after the whole file had been
 * uploaded. The same rule, and the same words, applied here before a byte moves.
 */
function duplicateReferenceIssues(
  refs: { ref: string; line: number }[],
  describe: (ref: string, line: number, first: number) => PrecheckIssue,
): PrecheckIssue[] {
  const seen = new Map<string, number>();
  const issues: PrecheckIssue[] = [];
  for (const { ref, line } of refs) {
    if (!ref) continue;
    const first = seen.get(ref);
    if (first !== undefined) {
      // Twenty is enough to show the shape of the problem; one bad export repeats forever.
      if (issues.length < 20) issues.push(describe(ref, line, first));
      continue;
    }
    seen.set(ref, line);
  }
  const total = refs.filter(({ ref }) => ref).length - seen.size;
  if (total > issues.length) {
    issues.push(
      blocking(
        `${total - issues.length} more returns repeat a reference`,
        'Only the first twenty are listed.',
      ),
    );
  }
  return issues;
}

async function precheckRows(
  headers: string[],
  rows: Record<string, unknown>[],
  formType: string | null,
  taxYear: number,
  onPhase: (phase: PrecheckPhase) => void,
): Promise<PrecheckResult> {
  const rowCount = rows.length;
  const issues: PrecheckIssue[] = [];

  if (headers.length === 0) {
    issues.push(blocking('This file has no header row', 'The first row has to name the columns.'));
  }
  if (rowCount === 0) {
    issues.push(blocking('This file has no data', 'There is a header row and nothing under it.'));
  }
  if (rowCount > MAX_ROWS) {
    issues.push(
      blocking(
        'This file has too many rows',
        `${rowCount.toLocaleString()} rows, and the limit is ${MAX_ROWS.toLocaleString()}.`,
      ),
    );
  }

  if (formType && !(formType in FORM_FIELDS)) {
    issues.push(blocking('This form is not one Tab Service files', formType));
  }

  // The same rule the server applies after the upload, applied before it.
  const refHeader = findRecordRefHeader(headers);
  if (refHeader) {
    issues.push(
      ...duplicateReferenceIssues(
        rows.map((row, index) => ({
          ref: String(row[refHeader] ?? '').trim(),
          line: index + 2, // row 1 is the header, as the person sees it in their sheet
        })),
        (ref, line, first) =>
          blocking(
            'Two returns share a reference',
            `${refHeader} "${ref}" is already used on row ${first}. Each return needs its own ` +
              'reference: it is what names this return if you correct or replace it later, so two ' +
              'returns cannot share one.',
            `row ${line}`,
          ),
      ),
    );
  }

  // The columns as the server will read them, the rows as the records it will file, and the
  // records through the same checks. Structural faults above make the rest meaningless, so
  // the mapping and totals are only worked out for a file that got this far.
  if (!formType || headers.length === 0 || rowCount === 0 || rowCount > MAX_ROWS || !(formType in FORM_FIELDS)) {
    return { ok: issues.length === 0, issues, detected: { recordCount: rowCount }, depth: 'structural' };
  }

  onPhase('checking');
  await paint();
  const canonicalNames = canonicalNamesFromJsonSchema(recordJsonSchema(formType, taxYear));
  const { mapping, unmapped } = detectColumnMapping(headers, canonicalNames, formType);
  // The columns a return cannot be built without, judged on what the headers were MAPPED to
  // rather than on the headers themselves. A customer's sheet says "Payer EIN", which maps to
  // FILER_FEDERAL_ID_NUMBER perfectly well; compared against the canonical name as text it
  // looked missing, and the file was refused for want of a column it had.
  const mapped = new Set(Object.values(mapping));
  const identity = FORM_IDENTITY[formType];
  for (const [what, candidates] of [
    ["the payer's federal ID number", identity?.filerTin ?? []],
    ["the recipient's taxpayer ID", identity?.recipientTin ?? []],
  ] as const) {
    if (candidates.length && !candidates.some((name) => mapped.has(name))) {
      issues.push(
        blocking(
          'A column this form needs is missing',
          `No column was read as ${what} (${candidates[0]}). Rename the column in your file, or add it.`,
        ),
      );
    }
  }

  const records = rows.map((row, index) => {
    const given = refHeader ? String(row[refHeader] ?? '').trim() : '';
    return rowToRecord(row, mapping, given || `row-${index + 2}`);
  });
  issues.push(...recordIssues(formType, taxYear, records));

  return {
    ok: issues.length === 0,
    issues,
    detected: { formType, taxYear, recordCount: rowCount },
    depth: 'full',
    mapping,
    unmapped,
    records,
    boxTotals: boxTotals(formType, taxYear, records),
  };
}

async function precheckCsv(
  file: File,
  formType: string | null,
  taxYear: number,
  onPhase: (phase: PrecheckPhase) => void,
): Promise<PrecheckResult> {
  const { parse } = await import('papaparse');
  const text = await file.text();
  onPhase('parsing');
  await paint();
  const parsed = parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
  const headers = parsed.meta.fields ?? [];
  return precheckRows(headers, parsed.data, formType, taxYear, onPhase);
}

async function precheckExcel(
  file: File,
  formType: string | null,
  taxYear: number,
  onPhase: (phase: PrecheckPhase) => void,
): Promise<PrecheckResult> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const first = workbook.SheetNames[0];
  if (!first) {
    return {
      ok: false,
      issues: [blocking('This workbook has no sheets', 'The first sheet is the one that is read.')],
      detected: {},
      depth: 'structural',
    };
  }
  onPhase('parsing');
  await paint();
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[first], { defval: '' });
  const headers = rows.length ? Object.keys(rows[0]) : [];
  return precheckRows(headers, rows, formType, taxYear, onPhase);
}

async function precheckIrisXml(file: File): Promise<PrecheckResult> {
  const { XMLParser } = await import('fast-xml-parser');
  const text = await file.text();
  const issues: PrecheckIssue[] = [];
  let count = 0;
  try {
    const parsed = new XMLParser({ ignoreAttributes: false }).parse(text) as Record<string, unknown>;
    if (!parsed || Object.keys(parsed).length === 0) {
      issues.push(blocking('This file is not XML', 'Nothing could be read out of it.'));
    }
    // Counted rather than validated: the IRS schema check needs a native library the browser
    // does not have, and Tab Service runs it on arrival.
    count = (text.match(/<\/(?:\w+:)?RecipientDetail>/g) ?? []).length;
    if (count === 0) {
      issues.push(
        blocking('There are no returns in this transmission', 'No RecipientDetail was found.'),
      );
    }
  } catch (error) {
    issues.push(
      blocking(
        'This file is not well formed XML',
        error instanceof Error ? error.message : 'It could not be parsed.',
      ),
    );
  }
  return { ok: issues.length === 0, issues, detected: { recordCount: count }, depth: 'structural' };
}

/** Check a file before a byte of it is uploaded. */
export async function precheckFile(
  file: File,
  options: {
    format: string;
    formType: string | null;
    taxYear: number;
    onPhase?: (phase: PrecheckPhase) => void;
  },
): Promise<PrecheckResult> {
  const onPhase = options.onPhase ?? (() => {});
  try {
    onPhase('reading');
    await paint();
    switch (options.format) {
      case 'pub1220':
        return await precheckPub1220(file, options.taxYear, onPhase);
      case 'csv':
        return await precheckCsv(file, options.formType, options.taxYear, onPhase);
      case 'xlsx':
        return await precheckExcel(file, options.formType, options.taxYear, onPhase);
      case 'iris_xml':
        return await precheckIrisXml(file);
      default:
        return { ok: true, issues: [], detected: {}, depth: 'structural' };
    }
  } catch (error) {
    // A file that cannot even be opened is a finding, not a crash.
    return {
      ok: false,
      issues: [
        blocking(
          'This file could not be read',
          error instanceof Error ? error.message : 'It may be corrupt, or not the format chosen.',
        ),
      ],
      detected: {},
      depth: 'structural',
    };
  }
}
